// ─── NOTIFICATION PREFERENCES ─────────────────────────────────────────────────
// Manages per-user notification category toggles and snooze from Supabase.
// Table: notification_preferences
//   user_id, global_enabled, dm_notifications, story_notifications,
//   channel_notifications, community_notifications, phone_notifications,
//   channel_push_mode, snooze_until, created_at, updated_at

import { supabase } from './supabaseClient.js';

// Default preferences (all enabled, no snooze)
export const DEFAULT_PREFS = {
  global_enabled: true,
  dm_notifications: true,
  story_notifications: true,
  channel_notifications: true,
  community_notifications: true,
  phone_notifications: true,
  // Unsolicited incoming-call ring. Was localStorage-only, so it was
  // per-device and did not survive clearing site data — Nicole could not
  // turn the calls off (2026-07-31). Synced like every other switch now.
  random_calls: true,
  // How channel messages arrive (2026-10-05). 'batched' is what everyone had
  // until now: General rooms buzz once per five-minute wave, plus the 10:00 /
  // 19:00 unread digest. 'instant' is every message in every room as it
  // lands, no waves, no channel digest — Nicole's pick ("just normal push
  // notifs in each channel"). The three producers read the column directly.
  channel_push_mode: 'batched',
  // Who may message you FIRST (2026-10-19, Haley: "turn off the random DMs
  // entirely or just allow them from specific chars"). One synced switch for
  // the cold outreach and the warm follow-up; the server asks the same row
  // through character_may_dm(). 'all' is what everyone had.
  character_dm_mode: 'all',
  character_dm_allowed: [],
  // v423: mode 'all_except' keeps its own block list, so turning one
  // character off while everyone is allowed no longer writes a 425-name
  // allow list (2026-10-19 audit).
  character_dm_blocked: [],
  snooze_until: null,
};

export const CHARACTER_DM_MODES = [
  { value: 'all', label: 'Any character', desc: 'Characters you have not met may introduce themselves, and the ones you talk to may text you first.' },
  { value: 'all_except', label: 'Everyone except the ones I untick', desc: 'Any character may message you first, apart from the ones you switch off.' },
  { value: 'selected', label: 'Only the ones I pick', desc: 'Only the characters you tick (in Settings or on their DM row) may message you first.' },
  { value: 'off', label: 'Never', desc: 'No character ever messages you first. You always open the conversation.' },
];

/** May this character message this writer first? Mirrors public.character_may_dm. */
export function characterMayDM(prefs, characterId) {
  const mode = prefs?.character_dm_mode || 'all';
  if (mode === 'off') return false;
  if (mode === 'selected') return (prefs?.character_dm_allowed || []).includes(characterId);
  if (mode === 'all_except') return !(prefs?.character_dm_blocked || []).includes(characterId);
  return true;
}

/**
 * The preference change behind one tap on a "may message me first" switch
 * (a DM row's pill, the roster picker, the Settings checklist). The switch
 * always means "this character may text first", whatever the mode:
 *   all        → untick one  → all_except, blocking just them
 *   all_except → tick/untick → the block list shrinks/grows; empty → all
 *   selected   → tick/untick → the allow list grows/shrinks
 *   off        → tick one    → selected, allowing just them
 * Pure, so every surface agrees by test. `rosterIds` is accepted for
 * compatibility and no longer needed.
 */
export function nextCharacterDmPrefs(prefs, characterId, _rosterIds = []) {
  const mode = prefs?.character_dm_mode || 'all';
  const allowed = new Set(prefs?.character_dm_allowed || []);
  const blocked = new Set(prefs?.character_dm_blocked || []);
  const on = characterMayDM(prefs, characterId);
  if (mode === 'selected') {
    if (on) allowed.delete(characterId); else allowed.add(characterId);
    return { character_dm_mode: 'selected', character_dm_allowed: [...allowed] };
  }
  if (mode === 'off') {
    allowed.add(characterId);
    return { character_dm_mode: 'selected', character_dm_allowed: [...allowed] };
  }
  // 'all' and 'all_except'
  if (on) blocked.add(characterId); else blocked.delete(characterId);
  return blocked.size
    ? { character_dm_mode: 'all_except', character_dm_blocked: [...blocked] }
    : { character_dm_mode: 'all', character_dm_blocked: [] };
}

// The two channel push modes, in the order the Settings panel shows them.
export const CHANNEL_PUSH_MODES = [
  { value: 'instant', label: 'Every message', desc: 'Each message buzzes as it lands, in every room. No bundles, no unread reminders.' },
  { value: 'batched', label: 'Bundled', desc: 'General rooms buzz once per wave of new messages, plus an unread reminder at 10am and 7pm.' },
];

// Category key mapping — used by sendPushNotification callers
// to specify which preference controls their notification type
export const NOTIF_CATEGORIES = {
  dm: 'dm_notifications',
  story: 'story_notifications',
  channel: 'channel_notifications',
  community: 'community_notifications',
  phone: 'phone_notifications',
};

/**
 * Load notification preferences for a user. Creates default row if none exists.
 * @param {string} userId
 * @returns {object} preferences object
 */
export async function loadNotifPrefs(userId) {
  if (!userId) return { ...DEFAULT_PREFS };
  try {
    const { data, error } = await supabase
      .from('notification_preferences')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.warn('[NotifPrefs] Load error:', error.message);
      return { ...DEFAULT_PREFS };
    }
    if (!data) {
      // No row yet — create one with defaults
      // upsert, not insert: two tabs booting at once raced to the unique key
      // (duplicate-key errors ×3 in 7 days, 2026-10-19 audit)
      const { data: inserted, error: insertErr } = await supabase
        .from('notification_preferences')
        .upsert({ user_id: userId }, { onConflict: 'user_id', ignoreDuplicates: false })
        .select()
        .single();
      if (insertErr) {
        console.warn('[NotifPrefs] Insert default error:', insertErr.message);
        return { ...DEFAULT_PREFS };
      }
      return extractPrefs(inserted);
    }
    return extractPrefs(data);
  } catch (err) {
    console.error('[NotifPrefs] Unexpected error:', err);
    return { ...DEFAULT_PREFS };
  }
}

/**
 * Save a partial update to notification preferences.
 * @param {string} userId
 * @param {object} updates — partial prefs object, e.g. { dm_notifications: false }
 */
export async function saveNotifPrefs(userId, updates) {
  if (!userId) return false;
  try {
    const { error } = await supabase
      .from('notification_preferences')
      .upsert({
        user_id: userId,
        ...updates,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'user_id' });

    if (error) {
      console.warn('[NotifPrefs] Save error:', error.message);
      return false;
    }
    return true;
  } catch (err) {
    console.error('[NotifPrefs] Save unexpected error:', err);
    return false;
  }
}

/**
 * Set snooze for a duration from now. Pass 0 or null to clear snooze.
 * @param {string} userId
 * @param {number|null} hours — hours to snooze (can be fractional, e.g. 0.5 for 30 min)
 */
export async function setSnooze(userId, hours) {
  const snoozeUntil = hours && hours > 0
    ? new Date(Date.now() + hours * 60 * 60 * 1000).toISOString()
    : null;
  return saveNotifPrefs(userId, { snooze_until: snoozeUntil });
}


/**
 * Check if a notification category is currently allowed for a user.
 * Checks: global toggle, category toggle, and snooze.
 * @param {object} prefs — the prefs object from loadNotifPrefs
 * @param {string} category — one of 'dm', 'story', 'channel', 'community', 'phone'
 * @returns {boolean}
 */
export function isNotifAllowed(prefs, category) {
  if (!prefs) return true; // No prefs loaded = allow (fail-open)
  // Global kill switch
  if (prefs.global_enabled === false) return false;
  // Snooze active?
  if (prefs.snooze_until) {
    const snoozeEnd = new Date(prefs.snooze_until).getTime();
    if (Date.now() < snoozeEnd) return false;
  }
  // Category toggle
  const key = NOTIF_CATEGORIES[category];
  if (key && prefs[key] === false) return false;
  return true;
}

/**
 * Check if snooze is currently active.
 * @param {object} prefs
 * @returns {{ active: boolean, remaining: string|null }}
 */
export function getSnoozeStatus(prefs) {
  if (!prefs?.snooze_until) return { active: false, remaining: null };
  const end = new Date(prefs.snooze_until).getTime();
  const now = Date.now();
  if (now >= end) return { active: false, remaining: null };
  const diffMs = end - now;
  const hours = Math.floor(diffMs / (60 * 60 * 1000));
  const mins = Math.floor((diffMs % (60 * 60 * 1000)) / (60 * 1000));
  if (hours > 0) return { active: true, remaining: `${hours}h ${mins}m` };
  return { active: true, remaining: `${mins}m` };
}

// ─── helpers ────────────────────────────────────────────────────────────────

function extractPrefs(row) {
  return {
    global_enabled: row.global_enabled ?? true,
    dm_notifications: row.dm_notifications ?? true,
    story_notifications: row.story_notifications ?? true,
    channel_notifications: row.channel_notifications ?? true,
    community_notifications: row.community_notifications ?? true,
    phone_notifications: row.phone_notifications ?? true,
    random_calls: row.random_calls ?? true,
    channel_push_mode: row.channel_push_mode === 'instant' ? 'instant' : 'batched',
    character_dm_mode: ['all', 'all_except', 'selected', 'off'].includes(row.character_dm_mode) ? row.character_dm_mode : 'all',
    character_dm_allowed: Array.isArray(row.character_dm_allowed) ? row.character_dm_allowed.filter(x => typeof x === 'string') : [],
    character_dm_blocked: Array.isArray(row.character_dm_blocked) ? row.character_dm_blocked.filter(x => typeof x === 'string') : [],
    snooze_until: row.snooze_until || null,
  };
}
